import React from 'react';
import { useParams, Link } from 'react-router-dom';

const PAGES = {
  terms: {
    title: 'Terms & Conditions',
    body: [
      ['Educational Purpose', 'Students agree that all courses are educational in nature. Course completion does not guarantee employment, independent contractor opportunities, professional licensure, government certification, business success, or income generation.'],
      ['Compliance', 'Students are responsible for complying with all applicable laws and regulations, including IRS requirements such as PTIN/EFIN registration where required.'],
      ['Intellectual Property', 'All academy materials are proprietary and protected by copyright. Unauthorized reproduction or distribution is prohibited.'],
      ['Account Security', 'Students are responsible for safeguarding their account credentials and notifying TRJ Academy of any unauthorized use.'],
      ['Modifications', 'TRJ Academy reserves the right to update these terms with reasonable notice.'],
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    body: [
      ['Information We Collect', 'Name, email, address, phone number, enrollment data, and course activity.'],
      ['How We Use Information', 'For enrollment, course delivery, certificates, student support, and platform operations.'],
      ['Sharing', 'Student information will never be sold. We use trusted service providers (e.g., Resend, PayPal) strictly to operate the platform.'],
      ['Your Rights', 'You may request access, correction, or deletion of your personal information by emailing support@trjacademy.com.'],
      ['Security', 'We implement reasonable safeguards. No system is 100% secure; please use strong passwords.'],
    ],
  },
  refund: {
    title: 'Refund Policy',
    body: [
      ['Digital Products', 'Digital educational products are generally non-refundable.'],
      ['72-Hour Window', 'Refund requests may be considered within 72 hours if no course content has been accessed, no materials downloaded, and no lessons viewed.'],
      ['Certifications', 'Certification programs become non-refundable once access is granted.'],
      ['How to Request', 'Email billing@trjacademy.com with your order number and reason.'],
    ],
  },
  disclaimer: {
    title: 'Disclaimer',
    body: [
      ['Independent Institution', 'TRJ Academy™ is an independent educational institution owned and operated by TRJ Services, Inc. TRJ Academy™ is not a college, university, governmental agency, licensing authority, or accrediting body.'],
      ['No Guarantees', 'Completion of any course or program does not guarantee employment, independent contractor opportunities, professional licensure, government certification, business success, or income generation.'],
      ['Not Affiliated', 'TRJ Academy™ is not affiliated with or endorsed by the IRS, Prometric, or any governmental agency unless explicitly stated for specific approved programs in the future.'],
      ['Educational Use Only', 'Educational materials are provided for informational and training purposes only and should not be considered legal, tax, accounting, or financial advice.'],
    ],
  },
  accessibility: {
    title: 'Accessibility Statement',
    body: [
      ['Commitment', 'TRJ Academy is committed to providing an accessible learning experience to all students.'],
      ['Standards', 'We strive to conform to WCAG 2.1 Level AA standards in our website and learning platform.'],
      ['Feedback', 'If you encounter accessibility barriers, please email support@trjacademy.com so we can assist promptly.'],
    ],
  },
};

export default function LegalPage() {
  const { page } = useParams();
  const content = PAGES[page];
  if (!content) return (
    <div className="min-h-screen flex items-center justify-center bg-trjlight">
      <div className="text-center">
        <div className="font-display font-semibold text-2xl text-navy">Page not found</div>
        <Link to="/" className="text-navy underline mt-2 inline-block">Return home</Link>
      </div>
    </div>
  );

  return (
    <div data-testid={`legal-${page}`} className="bg-white">
      <section className="bg-navy text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 hero-grain opacity-50" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
          <span className="trj-divider">Legal</span>
          <h1 className="font-display font-semibold text-4xl lg:text-5xl mt-5">{content.title}</h1>
          <p className="text-white/70 mt-3 text-sm">Last updated {new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </section>
      <article className="max-w-4xl mx-auto px-6 lg:px-8 py-16 space-y-10">
        {content.body.map(([heading, body], i) => (
          <section key={i}>
            <h2 className="font-display font-semibold text-xl text-navy">{heading}</h2>
            <p className="text-slate-700 mt-3 leading-relaxed">{body}</p>
          </section>
        ))}
        <div className="border-t border-slate-200 pt-8 text-sm text-slate-500 font-serif italic">
          TRJ Academy™ — Knowledge • Integrity • Leadership
        </div>
      </article>
    </div>
  );
}
